import * as React from 'react';
import {
  Box,
  Button,
  FormField,
  Icon,
  TextInput,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

export interface SearchFormProps {
  /** The unique ID for the search input and its associated label. */
  id: string;
  /** Label for the search input field. */
  label: string;
  /** The *initial* value of the search input. Subsequent changes are handled internally by the component. */
  initialValue?: string;
  /** Expands the form to fill its container instead of using the standard max width. */
  fullWidth?: boolean;
  /** Callback function executed when the form is submitted (Enter key, search button) or cleared. Receives the current search term (or an empty string on clear). */
  onSearchSubmit: (value: string) => void;
}

/**
 * A reusable search form component using
 * It renders a text input field (`type="search"`) styled with a leading search icon and a trailing search button that calls onSearchSubmit.
 *
 * The component manages the input's state internally. The `initialValue` prop is used
 * only to set the value when the component first mounts. User interactions update the
 * internal state, and the `onSearch` callback is triggered upon submission or clearing.
 *
 */

export const SearchForm: React.FC<SearchFormProps> = ({
  label,
  id,
  initialValue,
  fullWidth = false,
  onSearchSubmit,
}) => {
  const [value, setValue] = React.useState(initialValue || '');
  const [inputHasFocus, setInputHasFocus] = React.useState(false);
  const { formatMessage } = useIntl();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearchSubmit(value);
  };

  const handleSearchClear = () => {
    setValue('');
    onSearchSubmit('');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleSearchButtonClick = () => {
    onSearchSubmit(value);
  };

  return (
    <Box
      as="form"
      role="search"
      onSubmit={handleSubmit}
      css={fullWidth ? { width: '100%' } : { maxWidth: '36rem' }}
    >
      <FormField
        id={id}
        label={label}
        hideLabel
        css={{
          '::-webkit-search-cancel-button': {
            display: 'none',
          },
        }}
      >
        <TextInput
          type="search"
          clearable={!!value && value.length > 0}
          onClear={handleSearchClear}
          value={value}
          onChange={handleSearchChange}
          onFocus={() => setInputHasFocus(true)}
          onBlur={() => setInputHasFocus(false)}
          leadingContent={
            <Icon
              icon="interfaceSearch"
              tint="$onSurfaceIconPrimary"
              size="1.25rem"
            />
          }
          trailingContent={
            <Button
              priority={inputHasFocus ? 'primary' : 'secondary'}
              quiet={!inputHasFocus}
              size="small"
              onClick={handleSearchButtonClick}
            >
              {formatMessage({ id: 'SEARCH' })}
            </Button>
          }
        />
      </FormField>
    </Box>
  );
};
